import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../../components/ui";
import {
  FaArrowRight, FaArrowLeft, FaBullhorn, FaCheckCircle, FaChevronRight,
  FaLightbulb, FaUsers, FaEnvelope, FaRandom, FaEdit, FaChartBar,
  FaReply, FaPlus, FaFilter, FaSearch, FaExclamationTriangle
} from "react-icons/fa";

export default function CampaignsGuide() {
  useEffect(() => {
    document.title = "Campaigns Guide | Walytic - WhatsApp Outreach Sequences";
  }, []);

  return (
    <div className="page-content">
      <PageHeader title="Campaigns Guide" subtitle="Send multi-step WhatsApp message sequences to your leads across one or more sessions." />

      <div className="max-w-4xl mx-auto">

        {/* Table of Contents */}
        <nav className="bg-surface-card rounded-xl border border-surface-border p-6 mb-12">
          <h2 className="font-semibold text-lg text-ink-primary mb-4">In this guide</h2>
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              { id: 'what-are-campaigns', label: 'What are Campaigns?' },
              { id: 'creating-a-campaign', label: 'Creating a campaign' },
              { id: 'multi-session-sending', label: 'Multi-session sending' },
              { id: 'targeting-leads-by-filter', label: 'Targeting leads by filter' },
              { id: 'editing-a-live-campaign', label: 'Editing a live campaign' },
              { id: 'tracking-results', label: 'Tracking results' },
              { id: 'reply-routing', label: 'Reply routing' },
              { id: 'tips', label: 'Tips and best practices' },
            ].map((item) => (
              <a key={item.id} href={`#${item.id}`} className="flex items-center text-sm text-ink-secondary hover:text-brand py-1.5 transition-colors">
                <FaChevronRight className="h-4 w-4 mr-1 text-ink-muted" />
                {item.label}
              </a>
            ))}
          </div>
        </nav>

        {/* Section: What are Campaigns? */}
        <section id="what-are-campaigns" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">What are Campaigns?</h2>
          <p className="text-ink-secondary mb-4 leading-relaxed">
            Outreach campaigns send multi-step WhatsApp message sequences to your leads. Unlike Flow Builder
            (which responds to incoming messages), Campaigns let you reach out first. You write the messages,
            pick the schedule, select your target leads, and Walytic handles delivery.
          </p>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            You can spread sending across multiple WhatsApp sessions to increase throughput. If you have three
            connected numbers, the system distributes leads among them automatically.
          </p>
          <ul className="space-y-3 mb-6">
            {[
              'Send a welcome drip to new signups over several days',
              'Follow up after a demo with a timed sequence of messages',
              'Run a promotional campaign across thousands of leads',
              'Re-engage cold contacts with a reactivation sequence',
              'Target specific lead segments by tag or pipeline stage',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-ink-secondary">
                <FaCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Section: Creating a Campaign */}
        <section id="creating-a-campaign" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Creating a campaign</h2>
          <p className="text-ink-secondary mb-6">
            Campaigns use a 4-step wizard. Each step collects one piece of the puzzle: settings, leads, messages, and a final review.
          </p>

          <div className="space-y-8">
            {/* Step 1 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-amber-600 text-white flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Setup</h3>
                <p className="text-ink-secondary mb-3">
                  Name your campaign and configure the basics.
                </p>
                <div className="bg-surface-card border border-surface-border rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-sm font-medium text-ink-primary">Campaign Name</p>
                    <p className="text-sm text-ink-muted">
                      Pick something descriptive, e.g., &quot;Q2 Product Launch&quot; or &quot;Post-Demo Follow-Up.&quot;
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-ink-primary">WhatsApp Sessions</p>
                    <p className="text-sm text-ink-muted">
                      Select one or more connected WhatsApp numbers using the checkboxes. Picking multiple sessions
                      lets the system distribute leads across them for higher throughput.
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-ink-primary">Duration</p>
                    <p className="text-sm text-ink-muted">
                      Choose the campaign window: <strong>1 Week</strong>, <strong>1 Month</strong>, <strong>1 Year</strong>, or <strong>Indefinite</strong>.
                      This controls how long the campaign keeps running. Indefinite means it continues until you stop it manually.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-amber-600 text-white flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Target Leads</h3>
                <p className="text-ink-secondary mb-3">
                  Choose which leads receive the campaign. Two modes are available:
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-surface-card border border-surface-border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FaSearch className="h-4 w-4 text-blue-600" />
                      <p className="text-sm font-medium text-ink-primary">Manual</p>
                    </div>
                    <p className="text-sm text-ink-muted">
                      Search your CRM by name or phone number and add leads one by one. Good for small, hand-picked lists.
                    </p>
                  </div>
                  <div className="bg-surface-card border border-surface-border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FaFilter className="h-4 w-4 text-violet-600" />
                      <p className="text-sm font-medium text-ink-primary">Filter</p>
                    </div>
                    <p className="text-sm text-ink-muted">
                      Select leads by tags and/or stages. Click Preview to see how many leads match before committing.
                      Best for large lists.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-amber-600 text-white flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Messages</h3>
                <p className="text-ink-secondary mb-3">
                  Build your message sequence with day-based scheduling. Each step in the sequence has:
                </p>
                <div className="bg-surface-card border border-surface-border rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-sm font-medium text-ink-primary">Message Text</p>
                    <p className="text-sm text-ink-muted mb-2">
                      Write your message. Supports variables like
                      {' '}{'{{firstName}}'}, {'{{phone}}'}, {'{{lastName}}'}, {'{{email}}'}, and any custom CRM fields.
                    </p>
                    <p className="text-sm text-ink-muted mb-2">
                      <strong>Multi-paragraph messages:</strong> press Enter to add line breaks. The whole message
                      ships as a single chat bubble &mdash; recipients see the same line breaks you typed.
                    </p>
                    <p className="text-sm text-ink-muted">
                      <strong>Spintax (recommended):</strong> wrap alternatives in
                      {' '}<code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{`{Hi|Hello|Hey}`}</code>
                      {' '}and the system picks one at random per recipient. This varies the surface phrasing across
                      your list, which dramatically reduces the &ldquo;same message blasted to thousands&rdquo;
                      signal that gets numbers banned. Alternatives can span multiple lines &mdash; just put each
                      pipe (<code>|</code>) on its own line if it helps readability. Click the
                      {' '}<strong>How to use spintax in this message</strong> link under the textarea for a full
                      reference.
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-ink-primary">Media URL (optional)</p>
                    <p className="text-sm text-ink-muted">
                      Attach an image, PDF, or video by pasting a URL. Use the Image to URL tool if you need to host a file first.
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-ink-primary">&quot;Send on day&quot; Number</p>
                    <p className="text-sm text-ink-muted">
                      Set which day this message fires. Day 0 means immediately after enrollment.
                      Day 3 means three days later. Space your steps to avoid overwhelming leads.
                    </p>
                  </div>
                </div>
                <div className="mt-4 bg-surface-card border border-surface-border rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-8 w-8 rounded-lg bg-amber-100 flex items-center justify-center">
                      <FaPlus className="h-4 w-4 text-amber-600" />
                    </div>
                    <h4 className="font-semibold text-ink-primary">Add more steps</h4>
                  </div>
                  <p className="text-ink-secondary text-sm">
                    Click <strong>Add Step</strong> to create additional messages in the sequence. A 3-step campaign works
                    well for quick follow-ups. For longer nurture flows, 5-10 steps spread over weeks gives leads time
                    to respond between messages.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-amber-600 text-white flex items-center justify-center text-sm font-bold">4</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Review</h3>
                <p className="text-ink-secondary">
                  See a full summary of your campaign: name, selected sessions, lead count, and all message steps with their
                  day numbers. You can optionally activate the campaign right away. When everything looks right, click <strong>Create</strong>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Multi-session Sending */}
        <section id="multi-session-sending" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Multi-session sending</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            When you select multiple WhatsApp sessions in Step 1, the system distributes leads across them automatically.
            Here is how the distribution logic works:
          </p>

          <div className="space-y-4 mb-6">
            <div className="bg-surface-card border border-surface-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FaUsers className="h-4 w-4 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Affinity matching</h3>
              </div>
              <p className="text-ink-secondary text-sm">
                If a session previously contacted a lead, the system prefers that same session. This keeps the conversation
                history on one number, so the lead sees a consistent thread.
              </p>
            </div>

            <div className="bg-surface-card border border-surface-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-violet-100 flex items-center justify-center">
                  <FaRandom className="h-4 w-4 text-violet-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Load balancing for new leads</h3>
              </div>
              <p className="text-ink-secondary text-sm">
                For leads that have never been contacted, the system picks the session with the fewest assigned leads
                and adds random jitter. This avoids predictable patterns and spreads the load evenly.
              </p>
            </div>

            <div className="bg-surface-card border border-surface-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center">
                  <FaCheckCircle className="h-4 w-4 text-green-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Automatic failover</h3>
              </div>
              <p className="text-ink-secondary text-sm mb-3">
                If a session disconnects mid-campaign, pending messages for that session automatically route to another
                connected session in the same campaign. No manual intervention needed.
              </p>
              <ul className="space-y-1.5 text-sm text-ink-secondary">
                <li className="flex items-start gap-2">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500 mt-2 flex-shrink-0"></span>
                  <span>The next attempt picks a different connected number from your campaign and ships from there.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500 mt-2 flex-shrink-0"></span>
                  <span>The lead&rsquo;s timeline on the analytics page shows a <strong>&ldquo;rerouted&rdquo;</strong> event so you can see what happened.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500 mt-2 flex-shrink-0"></span>
                  <span>If <em>every</em> session in the campaign is offline, leads wait safely &mdash; the first one to reconnect picks them up.</span>
                </li>
              </ul>
            </div>

            <div className="bg-surface-card border border-surface-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-rose-100 flex items-center justify-center">
                  <FaCheckCircle className="h-4 w-4 text-rose-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Smart pacing per number</h3>
              </div>
              <p className="text-ink-secondary text-sm">
                Each WhatsApp number sends with a small randomized gap between messages to keep activity looking natural
                and reduce ban risk. When you enroll a large list, the campaign engine spreads the load so no single
                number gets overloaded &mdash; if one queue grows too long, new sends for that number are automatically
                deferred until it catches up.
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <FaExclamationTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                Make sure all selected sessions are connected before launching. Go to <strong>WhatsApp Phones</strong> and
                check the status of each number.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Targeting Leads by Filter */}
        <section id="targeting-leads-by-filter" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Targeting leads by filter</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            In Step 2 of the wizard, switch to <strong>Filter</strong> mode to target leads based on their CRM attributes
            instead of adding them manually.
          </p>

          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-amber-600 text-white flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Select tags</h3>
                <p className="text-ink-secondary">
                  Pick one or more tags, e.g., &quot;hot&quot;, &quot;enterprise&quot;, &quot;webinar-attendee&quot;.
                  Only leads that have at least one of the selected tags will be included.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-amber-600 text-white flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Select stages</h3>
                <p className="text-ink-secondary">
                  Optionally filter by pipeline stage, e.g., &quot;new&quot;, &quot;contacted&quot;, &quot;qualified&quot;.
                  You can combine tags and stages to narrow down the list further.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-amber-600 text-white flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Preview the count</h3>
                <p className="text-ink-secondary">
                  Click <strong>Preview</strong> to see exactly how many leads match your filter criteria. When the campaign
                  runs, it enrolls all matching leads automatically.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Editing a Live Campaign */}
        <section id="editing-a-live-campaign" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Editing a live campaign</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            You can edit campaigns even while they are active. There is no need to pause first.
          </p>

          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-surface-card border border-surface-border rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FaEnvelope className="h-4 w-4 text-blue-600" />
                </div>
                <h4 className="font-semibold text-sm text-ink-primary">Change messages</h4>
              </div>
              <p className="text-ink-secondary text-sm">
                Update message text, add media URLs, or adjust day numbers. Future sends use the updated content.
              </p>
            </div>

            <div className="bg-surface-card border border-surface-border rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center">
                  <FaPlus className="h-4 w-4 text-green-600" />
                </div>
                <h4 className="font-semibold text-sm text-ink-primary">Add sessions</h4>
              </div>
              <p className="text-ink-secondary text-sm">
                Add more WhatsApp numbers to increase sending capacity without restarting the campaign.
              </p>
            </div>

            <div className="bg-surface-card border border-surface-border rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-violet-100 flex items-center justify-center">
                  <FaFilter className="h-4 w-4 text-violet-600" />
                </div>
                <h4 className="font-semibold text-sm text-ink-primary">Update targeting</h4>
              </div>
              <p className="text-ink-secondary text-sm">
                Switch between Manual and Filter mode or change your tag/stage criteria to adjust the audience.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Tracking Results */}
        <section id="tracking-results" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Tracking results</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Click any campaign to open its detail page. You will see delivery metrics and per-session performance.
          </p>

          <div className="space-y-6">
            <div className="bg-surface-card border border-surface-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FaChartBar className="h-4 w-4 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Campaign-level stats</h3>
              </div>
              <p className="text-ink-secondary text-sm mb-3">
                The top of the detail page shows four numbers with percentages:
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {['Total Leads', 'Sent', 'Delivered', 'Replied'].map((stat) => (
                  <div key={stat} className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-ink-muted">{stat}</p>
                    <p className="text-lg font-bold text-ink-primary">--</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-surface-card border border-surface-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-teal-100 flex items-center justify-center">
                  <FaUsers className="h-4 w-4 text-teal-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Per-session breakdown</h3>
              </div>
              <p className="text-ink-secondary text-sm">
                A table shows each WhatsApp number's performance: how many leads it was assigned, how many messages it sent,
                delivery count, and reply count. Use this to spot if one session is underperforming or disconnected.
              </p>
            </div>

            <div className="bg-surface-card border border-surface-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <FaEnvelope className="h-4 w-4 text-amber-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Executions list</h3>
              </div>
              <p className="text-ink-secondary text-sm">
                Below the stats, the executions list shows each lead's individual status: pending, sent, delivered, or replied.
                You can scroll through to check on specific contacts.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Reply Routing */}
        <section id="reply-routing" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Reply routing</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            When a lead replies to a campaign message, two things happen:
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-surface-card border border-surface-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center">
                  <FaReply className="h-4 w-4 text-green-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Appears in Unibox</h3>
              </div>
              <p className="text-ink-secondary text-sm">
                The reply shows up in your Unibox inbox on the same WhatsApp session that sent the original message.
                You can continue the conversation from there like any other chat.
              </p>
            </div>

            <div className="bg-surface-card border border-surface-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <FaBullhorn className="h-4 w-4 text-amber-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Marked in campaign</h3>
              </div>
              <p className="text-ink-secondary text-sm">
                The campaign detail page marks that lead as &quot;replied.&quot; This updates the reply count in your
                campaign-level stats and shows in the executions list.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Tips */}
        <section id="tips" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-6">Tips and best practices</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Start with 2-3 sessions to test', text: 'Before scaling up, run a small campaign with a few sessions to confirm everything is connected and messages are delivering correctly.' },
              { title: 'Keep messages personalized', text: 'Use variables like {{firstName}} and {{phone}} in your messages. Personalized messages get noticeably higher reply rates.' },
              { title: 'Use Filter mode for large lists', text: 'If you are targeting hundreds or thousands of leads, Filter mode with tags and stages is faster than searching and adding leads one by one.' },
              { title: 'Monitor per-session stats', text: 'Check the per-session breakdown table regularly to make sure leads are distributed evenly. If one number has zero deliveries, it may be disconnected.' },
              { title: 'Space messages at least 1 day apart', text: 'Sending multiple messages on the same day can feel aggressive. A schedule of Day 0, Day 1, Day 3, Day 7 gives leads breathing room.' },
              { title: 'Review before activating', text: 'Use Step 4 (Review) to double-check your sessions, lead count, and message content. Catching a typo before launch saves you from sending corrections.' },
            ].map((tip, i) => (
              <div key={i} className="bg-surface-card border border-surface-border rounded-lg p-4">
                <h4 className="font-semibold text-sm text-ink-primary mb-1">{tip.title}</h4>
                <p className="text-sm text-ink-secondary">{tip.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Prev/Next navigation */}
        <div className="flex items-center justify-between border-t border-surface-border pt-8">
          <Link to="/guide/ai-features" className="text-sm text-ink-muted hover:text-ink-primary flex items-center gap-1">
            <FaArrowLeft className="h-4 w-4" /> Prev: AI Features
          </Link>
          <Link to="/guide/crm" className="text-sm font-medium text-brand hover:opacity-80 flex items-center gap-1">
            Next: CRM <FaArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
